import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { fetchProject, fetchProjects } from "../lib/api";

export default function ProjectDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [all, setAll] = useState([]);
    const [error, setError] = useState(false);

    useEffect(() => {
        setProject(null);
        setError(false);
        fetchProject(slug).then(setProject).catch(() => setError(true));
        fetchProjects().then(setAll).catch(() => {});
        window.scrollTo({ top: 0 });
    }, [slug]);

    const { prev, next } = useMemo(() => {
        if (!project || !all.length) return { prev: null, next: null };
        const i = all.findIndex((p) => p.slug === project.slug);
        if (i < 0) return { prev: null, next: null };
        return {
            prev: all[(i - 1 + all.length) % all.length],
            next: all[(i + 1) % all.length],
        };
    }, [project, all]);

    if (error) {
        return (
            <main className="pt-40 px-6 md:px-12 lg:px-24 pb-32" data-testid="project-not-found">
                <h1 className="serif text-4xl">Project not found</h1>
                <Link to="/projects" className="asc-link mt-6 inline-block">Back to projects</Link>
            </main>
        );
    }
    if (!project) {
        return (
            <main className="pt-40 px-6 md:px-12 lg:px-24 pb-32" data-testid="project-loading">
                <p className="asc-overline">Loading…</p>
            </main>
        );
    }

    const gallery = project.gallery || [];
    const galleryFocals = project.gallery_focals || [];
    const heroSrc = project.hero_image || project.image;
    const heroFocal = { x: 50, y: 50, zoom: 1, ...(project.hero_image_focal || project.image_focal || {}) };
    const focalStyle = (f) => {
        const p = { x: 50, y: 50, zoom: 1, ...(f || {}) };
        return {
            objectPosition: `${p.x}% ${p.y}%`,
            transform: `scale(${p.zoom})`,
            transformOrigin: `${p.x}% ${p.y}%`,
        };
    };

    return (
        <main data-testid="page-project-detail" className="bg-[#F9F8F6]">
            <Seo
                path={`/projects/${project.slug}`}
                title={`${project.title} — ${project.category}, ${project.location}`}
                description={project.summary}
                image={project.image}
                type="article"
            />
            {/* FULL-BLEED HERO */}
            <section className="relative h-[100svh] min-h-[560px] flex items-end overflow-hidden">
                <img
                    src={heroSrc}
                    alt={project.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={focalStyle(heroFocal)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-[#1A1A1A]/15 to-transparent" />
                <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 pb-14 md:pb-20">
                    <Reveal>
                        <p className="asc-overline text-[#E5E1DB]">
                            {project.category} · {project.location}
                        </p>
                    </Reveal>
                    <Reveal delay={120}>
                        <h1
                            data-testid="project-title"
                            className="serif text-[#F9F8F6] mt-4 text-5xl sm:text-6xl lg:text-[6rem] leading-[0.98] tracking-tight font-light"
                        >
                            {project.title}
                        </h1>
                    </Reveal>
                </div>
            </section>

            {/* BACK LINK */}
            <section className="px-6 md:px-12 lg:px-24 pt-10">
                <Link
                    to="/projects"
                    data-testid="back-to-projects"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]"
                >
                    <ArrowLeft size={14} /> All projects
                </Link>
            </section>

            {/* FACTS + NARRATIVE */}
            <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-4">
                        <p className="asc-overline">Project facts</p>
                        <dl className="mt-8 border-t border-[#E5E1DB]">
                            <Row k="Client" v={project.client} />
                            <Row k="Location" v={project.location} />
                            <Row
                                k="Category"
                                v={
                                    (project.categories && project.categories.length > 0
                                        ? project.categories
                                        : project.primary_category
                                            ? [project.primary_category]
                                            : [project.category]
                                    ).join(" · ")
                                }
                            />
                            {project.industry && <Row k="Industry" v={project.industry} />}
                            <Row k="Size" v={project.size} />
                            <Row k="Year" v={String(project.year)} />
                            {project.services && project.services.length > 0 && (
                                <Row k="Services" v={project.services.join(", ")} />
                            )}
                            {project.scope?.length > 0 && <Row k="Scope" v={project.scope.join(", ")} />}
                            {project.awards && project.awards.length > 0 && (
                                <div
                                    data-testid="facts-awards"
                                    className="grid grid-cols-12 py-6 gap-4 border-b border-[#E5E1DB]"
                                >
                                    <span className="col-span-12 md:col-span-4 asc-overline text-[#8C7A6B]">Awards</span>
                                    <div className="col-span-12 md:col-span-8 space-y-10">
                                        {project.awards.map((a, i) => {
                                            const inner = (
                                                <div
                                                    data-testid={`facts-award-${i}`}
                                                    className="flex flex-col items-start gap-3"
                                                >
                                                    {a.logo && (
                                                        <img
                                                            src={a.logo}
                                                            alt={a.organization || a.name}
                                                            /* Vertical layout — logo on top, transparent,
                                                               no white background, no border. */
                                                            className="h-24 md:h-28 w-auto max-w-[220px] object-contain"
                                                        />
                                                    )}
                                                    {a.organization && (
                                                        <p className="text-[11px] uppercase tracking-[0.24em] text-[#8C7A6B]">
                                                            {a.organization}
                                                        </p>
                                                    )}
                                                    {a.name && (
                                                        /* Award title uses the same font family, weight
                                                           and size as the Scope body copy (not the serif
                                                           heading font). */
                                                        <p className="text-base leading-relaxed text-[#1A1A1A] font-normal">
                                                            {a.name}
                                                        </p>
                                                    )}
                                                    {a.year && (
                                                        <p className="text-sm text-[#5C5855]">{a.year}</p>
                                                    )}
                                                </div>
                                            );
                                            return a.link ? (
                                                <a
                                                    key={i}
                                                    href={a.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block hover:opacity-80 transition-opacity"
                                                >
                                                    {inner}
                                                </a>
                                            ) : (
                                                <div key={i}>{inner}</div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </dl>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <p className="asc-overline">Approach</p>
                        <p
                            data-testid="project-summary"
                            className="mt-8 serif italic font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.15] tracking-tight text-[#1A1A1A]"
                        >
                            {project.summary}
                        </p>
                        <div className="h-px w-full bg-[#E5E1DB] my-12" />
                        <p
                            data-testid="project-story"
                            className="text-lg lg:text-xl text-[#1A1A1A] leading-[1.7] font-light whitespace-pre-line"
                        >
                            {project.story}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-12">
                            {project.tags.map((t) => (
                                <span
                                    key={t}
                                    className="px-4 py-2 border border-[#E5E1DB] text-[11px] uppercase tracking-[0.22em] text-[#5C5855]"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* EDITORIAL GALLERY — layout: [1] full, [2]+[3] pair, [4] full, [5] full. */}
            {gallery.length > 0 && (
                <section
                    data-testid="project-gallery"
                    className="pb-24 md:pb-32 px-6 md:px-12 lg:px-24 space-y-6 md:space-y-8"
                >
                    {gallery[0] && (
                        <Reveal>
                            <div className="asc-img-zoom w-full aspect-[16/9] overflow-hidden">
                                <img
                                    src={gallery[0]}
                                    alt={`${project.title} — image 1`}
                                    data-testid="gallery-image-0"
                                    className="w-full h-full object-cover"
                                    style={focalStyle(galleryFocals[0])}
                                    loading="eager"
                                />
                            </div>
                        </Reveal>
                    )}
                    {(gallery[1] || gallery[2]) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                            {gallery[1] && (
                                <Reveal className="asc-img-zoom aspect-[4/3]">
                                    <img
                                        src={gallery[1]}
                                        alt={`${project.title} — image 2`}
                                        data-testid="gallery-image-1"
                                        className="w-full h-full object-cover"
                                        style={focalStyle(galleryFocals[1])}
                                        loading="lazy"
                                    />
                                </Reveal>
                            )}
                            {gallery[2] && (
                                <Reveal delay={120} className="asc-img-zoom aspect-[4/3]">
                                    <img
                                        src={gallery[2]}
                                        alt={`${project.title} — image 3`}
                                        data-testid="gallery-image-2"
                                        className="w-full h-full object-cover"
                                        style={focalStyle(galleryFocals[2])}
                                        loading="lazy"
                                    />
                                </Reveal>
                            )}
                        </div>
                    )}
                    {gallery[3] && (
                        <Reveal>
                            <div className="asc-img-zoom w-full aspect-[16/9] overflow-hidden">
                                <img
                                    src={gallery[3]}
                                    alt={`${project.title} — image 4`}
                                    data-testid="gallery-image-3"
                                    className="w-full h-full object-cover"
                                    style={focalStyle(galleryFocals[3])}
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    )}
                    {gallery[4] && (
                        <Reveal>
                            <div className="asc-img-zoom w-full aspect-[16/9] overflow-hidden">
                                <img
                                    src={gallery[4]}
                                    alt={`${project.title} — image 5`}
                                    data-testid="gallery-image-4"
                                    className="w-full h-full object-cover"
                                    style={focalStyle(galleryFocals[4])}
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    )}
                </section>
            )}

            {/* PROJECT NAV — Previous / Next */}
            {(prev || next) && (
                <section
                    data-testid="project-nav"
                    className="border-t border-[#E5E1DB] px-6 md:px-12 lg:px-24 py-16 md:py-20"
                >
                    <div className="flex flex-col md:flex-row md:items-stretch md:justify-between gap-6">
                        {prev ? (
                            <Link
                                to={`/projects/${prev.slug}`}
                                data-testid="prev-project-link"
                                className="group flex-1 inline-flex items-center justify-start gap-4 border border-[#E5E1DB] hover:border-[#1A1A1A] px-8 py-8 transition-colors"
                            >
                                <ArrowLeft size={18} className="shrink-0 text-[#8C7A6B] transition-transform group-hover:-translate-x-1" />
                                <div className="text-left">
                                    <p className="asc-overline text-[#8C7A6B]">Previous project</p>
                                    <p className="serif text-xl lg:text-2xl mt-2 tracking-tight font-light">
                                        {prev.title}
                                    </p>
                                </div>
                            </Link>
                        ) : <span className="flex-1" />}
                        {next ? (
                            <Link
                                to={`/projects/${next.slug}`}
                                data-testid="next-project-link"
                                className="group flex-1 inline-flex items-center justify-end gap-4 border border-[#E5E1DB] hover:border-[#1A1A1A] px-8 py-8 transition-colors"
                            >
                                <div className="text-right">
                                    <p className="asc-overline text-[#8C7A6B]">Next project</p>
                                    <p className="serif text-xl lg:text-2xl mt-2 tracking-tight font-light">
                                        {next.title}
                                    </p>
                                </div>
                                <ArrowRight size={18} className="shrink-0 text-[#8C7A6B] transition-transform group-hover:translate-x-1" />
                            </Link>
                        ) : <span className="flex-1" />}
                    </div>
                </section>
            )}
        </main>
    );
}

const Row = ({ k, v }) => (
    <div className="grid grid-cols-12 gap-4 py-4 border-b border-[#E5E1DB]">
        <span className="col-span-4 asc-overline">{k}</span>
        <span className="col-span-8 text-[#1A1A1A]">{v}</span>
    </div>
);
